import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { getAllCheckIn, printDcCheckin } from "../../services/CheckInServices";
import { displayError } from "../../helpers";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { buildCheckInQueryParams } from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";
import { bootstrapListViewLoad } from "../../utils/listViewBootstrap";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatQty, sumField, splitRecordsByStatus, buildCancelledSummaryCard } from "../../utils/listSummaryUtils";
import moment from "moment";

function formatStatusLabel(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return raw.replace(/_/g, ' ').toUpperCase();
}

function CheckInTable() {
    const InitialData = {
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const todayDateLabel = moment().format('DD-MM-YYYY');

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(() => {
        const todayRecords = activeRecords.filter((row) => row.delivery_challan_date === todayDateLabel);
        return buildGradientCard('Today DC', formatQty(sumField(todayRecords, 'dc_qty')), 'today');
    }, [activeRecords, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('DC Qty', formatQty(sumField(activeRecords, 'dc_qty')), 'blue'),
            buildGradientCard('Accepted', formatQty(sumField(activeRecords, 'accepted_qty')), 'teal'),
        ];
        const cancelledCard = buildCancelledSummaryCard(cancelledRecords, {
            valueField: 'dc_qty',
            entityLabel: 'check-in cancelled',
        });
        if (cancelledCard) cards.push(cancelledCard);
        return cards;
    }, [activeRecords, cancelledRecords]);

    const cancelledPopup = useMemo(() => ({
        title: `Cancelled Check-Ins (${cancelledRecords.length})`,
        emptyMessage: 'No cancelled check-ins in the current list.',
        columns: [
            { key: 'delivery_challan_no', label: 'DC No', align: 'center' },
            { key: 'delivery_challan_date', label: 'DC Date', align: 'center' },
            { key: 'customer_name', label: 'Customer', align: 'left' },
            { key: 'consignee_name', label: 'Site', align: 'left' },
            { key: 'vehicle_no', label: 'Vehicle', align: 'left' },
            { key: 'dc_qty', label: 'DC Qty', align: 'right' },
            { key: 'customer_status', label: 'Status', align: 'center' },
        ],
        getRows: () => cancelledRecords,
        mapRow: (row) => ({
            delivery_challan_no: row.delivery_challan_no,
            delivery_challan_date: row.delivery_challan_date,
            customer_name: row.customer_name,
            consignee_name: row.consignee_name,
            vehicle_no: row.vehicle_no,
            dc_qty: formatQty(row.dc_qty),
            customer_status: row.customer_status,
        }),
        footerRowBuilder: (rows) => ({
            delivery_challan_no: 'Total',
            delivery_challan_date: '',
            customer_name: '',
            consignee_name: '',
            vehicle_no: `${rows.length} DC`,
            dc_qty: formatQty(sumField(rows, 'dc_qty')),
            customer_status: '',
        }),
    }), [cancelledRecords]);

    const columns = useMemo(
        () => [
            { fieldName: "plant", headerName: 'Plant' },
            { fieldName: "checkout_no", headerName: 'CheckOut No.' },
            { fieldName: "delivery_challan_no", headerName: 'DC Nos' },
            { fieldName: "delivery_challan_date", headerName: 'DC Date' },
            { fieldName: "order_no", headerName: 'SO Nos' },
            { fieldName: 'customer_name', headerName: 'Billing Name' },
            { fieldName: "consignee_name", headerName: 'Site Name' },
            { fieldName: "grade", headerName: 'Grade' },
            { fieldName: "dc_qty", headerName: 'DC Qty' },
            { fieldName: "accepted_qty", headerName: 'Accepted Qty' },
            { fieldName: "customer_status", headerName: 'Customer Status' },
            { fieldName: "cus_dc_status", headerName: 'DC Status' },
            { fieldName: "vehicle_no", headerName: 'Vehicle No' },
            { fieldName: 'driver_name', headerName: 'Drivers Name' },
            { fieldName: "id", headerName: "ROW_ACTIONS" },
        ],
        [],
    );

    const loadCheckInList = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return Promise.resolve();
        }
        const activeConfig = config || getDefaultListViewConfig('check_in');
        setIsLoading(true);
        return getAllCheckIn(cookies, {
            ...buildCheckInQueryParams(activeConfig),
            limit: 500,
        })
            .then((checkinList) => {
                const tableData = (checkinList?.checkin_list || []).map((checkin) => {
                    const dc = checkin.delivery_challan || {};
                    const so = dc.work_schedule?.sales_order_detail?.sales_order || {};
                    const consignee = so.consignee || {};
                    return {
                    id: checkin.id,
                    checkout_no: checkin.checkout_no,
                    delivery_challan_no: dc.delivery_challan_no,
                    delivery_challan_date: dc.delivery_challan_date,
                    order_no: so.order_no,
                    customer_name: consignee.customer?.name,
                    consignee_name: consignee.name,
                    grade: dc.work_schedule?.sales_order_detail?.product?.name,
                    dc_qty: dc.dc_qty,
                    accepted_qty: checkin.accepted_qty,
                    customer_status: formatStatusLabel(checkin.customer_status || 'Waiting for checkin'),
                    cus_dc_status: formatStatusLabel(checkin.cus_dc_status || 'Waiting for checkin'),
                    // Used by table row highlight for cancelled check-ins
                    status: String(checkin.customer_status || checkin.cus_dc_status || '')
                        .trim()
                        .toLowerCase() === 'cancelled'
                        ? 'Cancelled'
                        : 'Active',
                    vehicle_no: dc.vehicle?.name || dc.customer_vehicle_no || '',
                    driver_name: checkin.driver_name,
                    hide_row_edit: checkin.customer_status ? "No" : "Yes",
                    plant: dc.work_schedule?.plant?.alias_name,
                    };
                });
                setData({ records: tableData });
            })
            .catch((error) => {
                setData({ records: [] });
                displayError(error.response?.data ?? error.message, 'Failed to load check-in list');
            })
            .finally(() => setIsLoading(false));
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadCheckInList(config);
    }, [loadCheckInList]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        bootstrapListViewLoad({
            cookies,
            viewKey: 'check_in',
            loadFn: loadCheckInList,
            isCancelled: () => cancelled,
        });
        return () => { cancelled = true; };
    }, [cookies, cookies.myToken, loadCheckInList, refreshKey]);

    const onEditCheckIn = (checkinId) => {
        navigate(`/CheckInEdit/${checkinId}/`);
    };

    const onPrintDeliveryChallan = (checkinId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printDcCheckin(cookies, checkinId)
            .then((response) => {
                openPdfResponse(response, { title: 'Delivery Challan', downloadFileName: 'delivery_challan.pdf' });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error, 'Print Failed');
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((c) => c.headerName),
            data.records.map((checkin) => ({
                plant: checkin.plant,
                checkout_no: checkin.checkout_no,
                delivery_challan_no: checkin.delivery_challan_no,
                delivery_challan_date: checkin.delivery_challan_date,
                order_no: checkin.order_no,
                customer_name: checkin.customer_name,
                consignee_name: checkin.consignee_name,
                grade: checkin.grade,
                dc_qty: checkin.dc_qty,
                accepted_qty: checkin.accepted_qty,
                customer_status: checkin.customer_status,
                cus_dc_status: checkin.cus_dc_status,
                vehicle_no: checkin.vehicle_no,
                driver_name: checkin.driver_name,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of CheckIn',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((checkin) => ({
                plant: checkin.plant,
                checkout_no: checkin.checkout_no,
                delivery_challan_no: checkin.delivery_challan_no,
                delivery_challan_date: checkin.delivery_challan_date,
                order_no: checkin.order_no,
                customer_name: checkin.customer_name,
                consignee_name: checkin.consignee_name,
                grade: checkin.grade,
                dc_qty: checkin.dc_qty,
                accepted_qty: checkin.accepted_qty,
                customer_status: checkin.customer_status,
                cus_dc_status: checkin.cus_dc_status,
                vehicle_no: checkin.vehicle_no,
                driver_name: checkin.driver_name,
            })),
            'CheckInList.pdf',
        );
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="GATE"
                    title="List of Check In"
                    viewKey="check_in"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/CheckIn"
                    addNewLabel="New Check In"
                />
                <ListPageGradientSummary
                    highlightCard={highlightCard}
                    cards={summaryCards}
                    cancelledPopup={cancelledPopup}
                />
                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='List of CheckIn'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditCheckIn}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintDeliveryChallan}
                            hidePrint={false}
                        />
                    </div>
                </div>
            </div>
</div>
    );
}

export default CheckInTable;
